import React, { useLayoutEffect, useState } from 'react'

import {
  collectResumePreviewFontSizeLabels,
  type ResumeFontSizeLabel,
} from '../../utils/resumePreviewFontSizeLabels'
import { useResumePreviewFitScale } from './resumePreviewFitScaleContext'

const BLOCK_LABEL_RU: Record<string, string> = {
  header: 'шапка',
  target: 'target title',
  summary: 'summary',
  section: 'секция',
  role: 'роль',
  bullet: 'буллет',
  skills: 'skills',
  right: 'правая кол.',
  other: 'прочее',
}

export function ResumePreviewFontSizeLegend({ labels = [] }: { labels?: ResumeFontSizeLabel[] }) {
  const uniquePt = Array.from(new Set(labels.map((l) => l.fontSizePt))).sort((a, b) => b - a)
  return (
    <div
      className="resume-preview-font-size-legend mb-3 w-full max-w-[210mm] rounded-md border border-violet-300 bg-violet-50 px-3 py-2 text-xs text-violet-950 shadow-sm"
      data-testid="resume-font-size-legend"
    >
      <p className="mb-1 font-medium">
        Режим fontSizes=1: кегль слева от блока (pt и px). Текст резюме не сдвигается.
      </p>
      {uniquePt.length > 0 ? (
        <p className="text-violet-900">
          Уникальных кеглей на листе: {uniquePt.length} — {uniquePt.map((pt) => `${pt}pt`).join(', ')}
        </p>
      ) : (
        <p className="text-violet-900">Смотри фиолетовые бейджи слева от блоков на каждом листе.</p>
      )}
    </div>
  )
}

type ResumePreviewFontSizeLabelsOverlayProps = {
  enabled?: boolean
  measureRootRef: React.RefObject<HTMLElement | null>
  scopeSelector?: string
  overlayHeightPx?: number
}

/**
 * Paint-only overlay: sibling of clipped content, never wraps resume DOM.
 * Badges sit in the left gutter (negative X) and do not affect line layout.
 */
export function ResumePreviewFontSizeLabelsOverlay({
  enabled = false,
  measureRootRef,
  scopeSelector,
  overlayHeightPx,
}: ResumePreviewFontSizeLabelsOverlayProps) {
  const fitScale = useResumePreviewFitScale()
  const [labels, setLabels] = useState<ResumeFontSizeLabel[]>([])

  useLayoutEffect(() => {
    if (!enabled) {
      setLabels([])
      return
    }
    const root = measureRootRef.current
    if (!root) return

    const update = () => {
      const el = measureRootRef.current
      if (!el) return
      setLabels(
        collectResumePreviewFontSizeLabels(el, {
          scopeSelector,
          coordinateScale: fitScale,
        })
      )
    }

    update()
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      void document.fonts.ready.then(update)
    }

    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(update)
    ro.observe(root)
    return () => ro.disconnect()
  }, [enabled, measureRootRef, scopeSelector, fitScale])

  if (!enabled || labels.length === 0) return null

  return (
    <div
      className="resume-preview-font-size-labels pointer-events-none absolute inset-x-0 top-0 z-[22] overflow-visible"
      style={{ height: overlayHeightPx ?? '100%' }}
      aria-hidden
      data-testid="resume-font-size-labels-layer"
    >
      {labels.map((item) => (
        <div
          key={item.id}
          className="resume-preview-font-size-badge absolute max-w-[52px] truncate rounded border border-violet-400/80 bg-violet-100/95 px-0.5 py-px text-[8px] font-mono leading-tight text-violet-950 shadow-sm"
          style={{
            top: item.labelTop,
            left: item.labelLeft,
          }}
          title={`${item.preview} · ${BLOCK_LABEL_RU[item.block] ?? item.block}`}
        >
          <span className="font-semibold">{item.fontSizePt}pt</span>
          <span className="opacity-75">/{item.fontSizePx}</span>
        </div>
      ))}
    </div>
  )
}
